import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FileCheck,
  FileX,
  Flag,
  TriangleAlert,
} from "lucide-react";
import {
  doctorHasNotOpenedStartedMeeting,
  MEETING_CALENDAR_STATUS_LABEL,
  meetingCalendarStatus,
  type ClinicianMeeting,
  type MeetingCalendarStatus,
} from "@care-by-ella/contracts";
import { useViewPath } from "@/components/SupportView";
import {
  addDays,
  clockTime,
  evidenceText,
  hourRange,
  isoDay,
  layoutColumns,
  meetingEnd,
  meetingKindLabel,
  minutesIntoDay,
  sameDay,
  startOfDay,
  weekDays,
} from "@/lib/meeting-calendar";
import { patientMeetingHref } from "@/lib/patient-links";
import { useNow } from "@/lib/use-now";
import { MeetingSidePanel } from "./MeetingSidePanel";
import { STATUS_ICON } from "./MeetingStatusTag";
import { useMeetingCalendar } from "./useMeetingCalendar";

// TES-1233 (Emelie 2026-10-11, TES-1236 point 1) — the default view under
// "Möten": "Rullande sjudagarsvy", "En kolumn per dag och tider vertikalt",
// "Dagens datum markeras tydligt", "En linje visar aktuell tid", "Knapp för
// I dag", "Navigation till föregående och nästa vecka".
//
// A small custom grid rather than a calendar library: seven absolutely
// positioned columns are all this needs, and the admin bundle stays as it is.

// 20-minute meetings must fit three short lines: time, patient, type.
const PX_PER_MIN = 2.4;

// Booked blocks: Emelie's table. Colour only repeats the icon and the word.
const BLOCK_CLASS: Record<MeetingCalendarStatus, string> = {
  booked: "bg-brand text-textOnBrand border-brand-deep",
  ongoing:
    "bg-brand text-textOnBrand border-brand-deep ring-2 ring-brand-deep ring-offset-1",
  completed: "bg-surfaceMuted text-textSecondary border-textTertiary",
  cancelled:
    "bg-surface text-textSecondary border-dashed border-textTertiary line-through",
  patient_no_show:
    "bg-status-incomplete-fill text-status-incomplete-ink border-status-incomplete-ink",
  needs_rebooking:
    "bg-status-risk-fill text-status-risk-ink border-status-risk-ink",
};

type Item =
  | {
      key: string;
      start: Date;
      end: Date;
      type: "meeting";
      meeting: ClinicianMeeting;
    }
  | { key: string; start: Date; end: Date; type: "free" };

function rangeLabel(days: Date[]): string {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const sameMonth = first.getMonth() === last.getMonth();
  const head = first.toLocaleDateString("sv-SE", {
    day: "numeric",
    ...(sameMonth ? {} : { month: "short" }),
  });
  const tail = last.toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${head}–${tail}`;
}

export function WeekCalendar({
  anchor,
  onAnchor,
  onUnauthorized,
}: {
  anchor: Date;
  onAnchor: (day: Date) => void;
  onUnauthorized: () => void;
}) {
  const now = useNow();
  const viewPath = useViewPath();
  const days = useMemo(() => weekDays(anchor), [anchor]);
  const from = days[0]!;
  const to = useMemo(() => addDays(from, 7), [from]);
  const { data, error, reload } = useMeetingCalendar(from, to, onUnauthorized);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef<string | null>(null);

  const selected = data?.meetings.find((m) => m.id === selectedId) ?? null;

  const closePanel = useCallback(() => {
    const id = selectedId;
    setSelectedId(null);
    if (id) {
      requestAnimationFrame(() =>
        document.getElementById(`meeting-block-${id}`)?.focus(),
      );
    }
  }, [selectedId]);

  const items = useMemo<Item[]>(() => {
    if (!data) return [];
    return [
      ...data.meetings.map<Item>((m) => ({
        key: m.id,
        start: new Date(m.scheduledAt),
        end: meetingEnd(m),
        type: "meeting",
        meeting: m,
      })),
      ...data.freeSlots.map<Item>((s) => {
        const start = new Date(s.startAt);
        return {
          key: `free-${s.doctorId}-${s.startAt}`,
          start,
          end: new Date(start.getTime() + s.durationMinutes * 60_000),
          type: "free",
        };
      }),
    ];
  }, [data]);

  const windows = useMemo(
    () =>
      (data?.windows ?? []).map((w) => ({
        start: new Date(w.startAt),
        end: new Date(w.endAt),
      })),
    [data],
  );

  const { firstHour, lastHour } = hourRange([...items, ...windows]);
  const axisStart = firstHour * 60;
  const height = (lastHour - firstHour) * 60 * PX_PER_MIN;
  const top = (d: Date) => (minutesIntoDay(d) - axisStart) * PX_PER_MIN;

  // Open on the current time (or the first thing in the week), once per week.
  useEffect(() => {
    const key = isoDay(from);
    if (!data || scrolledFor.current === key || !scrollRef.current) return;
    scrolledFor.current = key;
    const today = new Date();
    const inWeek = days.some((d) => sameDay(d, today));
    const firstItem = [...items].sort(
      (a, b) => minutesIntoDay(a.start) - minutesIntoDay(b.start),
    )[0];
    const target = inWeek ? today : firstItem?.start;
    if (target) {
      scrollRef.current.scrollTop = Math.max(0, top(target) - 80);
    }
    // `top` is derived from the axis, which `items` already captures.
  }, [data, from, days, items]);

  const today = new Date(now);

  return (
    <div className="flex flex-col gap-md lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-sm">
        <div className="flex flex-wrap items-center gap-sm">
          <button
            type="button"
            onClick={() => onAnchor(startOfDay(new Date()))}
            className="rounded-md border border-surfaceMuted px-md py-xs text-sm hover:border-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            I dag
          </button>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => onAnchor(addDays(from, -7))}
              className="rounded-md p-xs hover:bg-surfaceMuted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <ChevronLeft aria-hidden className="h-5 w-5" />
              <span className="sr-only">Föregående vecka</span>
            </button>
            <button
              type="button"
              onClick={() => onAnchor(addDays(from, 7))}
              className="rounded-md p-xs hover:bg-surfaceMuted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <ChevronRight aria-hidden className="h-5 w-5" />
              <span className="sr-only">Nästa vecka</span>
            </button>
          </div>
          <h2 className="font-display text-lg" aria-live="polite">
            {rangeLabel(days)}
          </h2>
          {!data && !error ? (
            <span className="text-sm text-textSecondary">Hämtar…</span>
          ) : null}
        </div>

        {error ? (
          <div
            role="alert"
            className="rounded-md border border-danger/30 bg-danger/10 p-md text-sm text-danger"
          >
            {error}
          </div>
        ) : null}

        <Legend />

        <div className="overflow-x-auto rounded-md border border-surfaceMuted bg-surface">
          <div className="min-w-[46rem]">
            {/* Day headers */}
            <div className="grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))] border-b border-surfaceMuted">
              <div />
              {days.map((d) => {
                const isToday = sameDay(d, today);
                return (
                  <div
                    key={isoDay(d)}
                    id={`day-${isoDay(d)}`}
                    aria-current={isToday ? "date" : undefined}
                    className={`px-xs py-sm text-center text-sm ${
                      isToday ? "bg-brand/10 font-semibold text-brand-deep" : ""
                    }`}
                  >
                    <span className="block capitalize">
                      {d.toLocaleDateString("sv-SE", { weekday: "short" })}
                    </span>
                    <span
                      className={`mx-auto mt-[2px] flex h-7 w-7 items-center justify-center rounded-full ${
                        isToday ? "bg-brand text-textOnBrand" : ""
                      }`}
                    >
                      {d.getDate()}
                    </span>
                    {isToday ? <span className="text-xs">I dag</span> : null}
                  </div>
                );
              })}
            </div>

            {/* Time grid */}
            <div ref={scrollRef} className="max-h-[70vh] overflow-y-auto">
              <div
                className="relative grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]"
                style={{ height }}
              >
                {/* Hour labels */}
                <div className="relative" aria-hidden>
                  {Array.from({ length: lastHour - firstHour }, (_, i) => (
                    <span
                      key={i}
                      className="absolute right-xs -translate-y-1/2 text-[11px] text-textTertiary"
                      style={{ top: i * 60 * PX_PER_MIN }}
                    >
                      {i === 0
                        ? ""
                        : `${String(firstHour + i).padStart(2, "0")}:00`}
                    </span>
                  ))}
                </div>

                {days.map((d) => {
                  const isToday = sameDay(d, today);
                  const dayItems = layoutColumns(
                    items.filter((it) => sameDay(it.start, d)),
                  );
                  const dayWindows = windows.filter((w) => sameDay(w.start, d));
                  return (
                    <section
                      key={isoDay(d)}
                      aria-labelledby={`day-${isoDay(d)}`}
                      className={`relative border-l border-surfaceMuted ${
                        isToday ? "bg-brand/5" : ""
                      }`}
                      style={{
                        // "Blockerad/ej bokningsbar tid: neutral markering" —
                        // everything outside a published window is hatched.
                        backgroundImage:
                          "repeating-linear-gradient(135deg, transparent 0 6px, rgba(120,120,120,0.08) 6px 8px)",
                      }}
                    >
                      {/* Hour lines */}
                      {Array.from({ length: lastHour - firstHour }, (_, i) => (
                        <div
                          key={i}
                          aria-hidden
                          className="absolute inset-x-0 border-t border-surfaceMuted"
                          style={{ top: i * 60 * PX_PER_MIN }}
                        />
                      ))}
                      {/* Published windows: the bookable frame, unhatched */}
                      {dayWindows.map((w) => (
                        <div
                          key={w.start.toISOString()}
                          aria-hidden
                          className="absolute inset-x-0 bg-surface"
                          style={{
                            top: top(w.start),
                            height:
                              ((w.end.getTime() - w.start.getTime()) / 60_000) *
                              PX_PER_MIN,
                          }}
                        />
                      ))}
                      <ul className="absolute inset-0">
                        {dayItems.map((it) => {
                          const style = {
                            top: top(it.start) + 1,
                            height: Math.max(
                              18,
                              ((it.end.getTime() - it.start.getTime()) /
                                60_000) *
                                PX_PER_MIN -
                                2,
                            ),
                            left: `calc(${(it.column / it.columns) * 100}% + 2px)`,
                            width: `calc(${100 / it.columns}% - 4px)`,
                          };
                          if (it.type === "free") {
                            return (
                              <li
                                key={it.key}
                                className="absolute overflow-hidden rounded-sm border border-brand/30 bg-brand/15 px-xs text-[11px] leading-[14px] text-brand-deep"
                                style={style}
                              >
                                <span className="sr-only">
                                  {`Ledig tid ${clockTime(it.start)}–${clockTime(it.end)}`}
                                </span>
                                <span aria-hidden>
                                  {clockTime(it.start)} Ledig tid
                                </span>
                              </li>
                            );
                          }
                          return (
                            <li key={it.key} className="absolute" style={style}>
                              <MeetingBlock
                                meeting={it.meeting}
                                now={now}
                                selected={selectedId === it.meeting.id}
                                onSelect={() => setSelectedId(it.meeting.id)}
                                openHref={viewPath(
                                  patientMeetingHref(
                                    it.meeting.patient.id,
                                    it.meeting.id,
                                  ),
                                )}
                              />
                            </li>
                          );
                        })}
                      </ul>
                      {/* "En linje visar aktuell tid" */}
                      {isToday &&
                      minutesIntoDay(today) >= axisStart &&
                      minutesIntoDay(today) <= lastHour * 60 ? (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-danger"
                          style={{ top: top(today) }}
                        >
                          <span className="absolute -left-[5px] -top-[6px] h-[10px] w-[10px] rounded-full bg-danger" />
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selected ? (
        <div className="fixed inset-x-0 bottom-0 z-30 max-h-[60vh] overflow-y-auto p-sm lg:static lg:z-auto lg:max-h-none lg:w-96 lg:shrink-0 lg:p-0">
          <MeetingSidePanel
            key={selected.id}
            meeting={selected}
            now={now}
            onClose={closePanel}
            onChanged={reload}
            onUnauthorized={onUnauthorized}
          />
        </div>
      ) : null}
    </div>
  );
}

function MeetingBlock({
  meeting,
  now,
  selected,
  onSelect,
  openHref,
}: {
  meeting: ClinicianMeeting;
  now: number;
  selected: boolean;
  onSelect: () => void;
  openHref: string;
}) {
  const status = meetingCalendarStatus(meeting, now);
  const StatusIcon = STATUS_ICON[status];
  const statusLabel = MEETING_CALENDAR_STATUS_LABEL[status];
  const kind = meetingKindLabel(meeting);
  const name =
    meeting.patient.name?.trim() || meeting.patient.personalNumberLast6;
  const flags = meeting.medicalFlagCount ?? 0;
  const notOpened = doctorHasNotOpenedStartedMeeting(meeting, now);
  const evidence =
    status === "booked" || status === "ongoing"
      ? evidenceText(meeting.evidence)
      : null;
  const time = clockTime(meeting.scheduledAt);

  const description = [
    `${time} ${name}`,
    kind,
    statusLabel,
    notOpened ? "Mötet har börjat men läkaren har inte öppnat det" : null,
    status === "ongoing" && meeting.patientJoinedAt
      ? "Patienten har anslutit"
      : null,
    flags > 0 ? `Medicinska flaggor: ${flags}` : null,
    evidence?.text ?? null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className={`relative h-full overflow-hidden rounded-sm border-l-4 text-[11px] leading-[14px] ${BLOCK_CLASS[status]} ${
        selected ? "outline outline-2 outline-offset-1 outline-textPrimary" : ""
      }`}
    >
      <button
        type="button"
        id={`meeting-block-${meeting.id}`}
        onClick={onSelect}
        aria-label={description}
        aria-expanded={selected}
        className="absolute inset-0 px-xs py-[2px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-textPrimary"
      >
        <span className="flex items-center gap-[3px] font-semibold">
          <StatusIcon aria-hidden className="h-3 w-3 shrink-0" />
          {time}
          {notOpened ? (
            <TriangleAlert aria-hidden className="h-3 w-3 shrink-0" />
          ) : null}
          {flags > 0 ? <Flag aria-hidden className="h-3 w-3 shrink-0" /> : null}
          {evidence ? (
            evidence.missing ? (
              <FileX aria-hidden className="h-3 w-3 shrink-0" />
            ) : (
              <FileCheck aria-hidden className="h-3 w-3 shrink-0" />
            )
          ) : null}
        </span>
        <span className="block truncate">{name}</span>
        {/* Room on the right for the running meeting's own CTA. */}
        <span
          className={`block truncate ${status === "ongoing" ? "pr-[4.75rem]" : ""}`}
        >
          {status === "booked" || status === "ongoing" ? kind : statusLabel}
        </span>
      </button>
      {status === "ongoing" ? (
        // Emelie: "Pågående: extra tydlig markering och CTA Öppna mötet".
        <Link
          to={openHref}
          className="absolute bottom-[2px] right-[2px] z-10 rounded-sm bg-surface px-xs text-[11px] font-semibold text-brand-deep no-underline hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-textPrimary"
        >
          Öppna mötet
        </Link>
      ) : null}
    </div>
  );
}

// Every kind of time the grid shows, so neither colour nor hatching has to be
// guessed.
function Legend() {
  const item = (swatch: string, label: string) => (
    <span className="inline-flex items-center gap-xs text-xs text-textSecondary">
      <span aria-hidden className={`h-3 w-3 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-md" aria-label="Förklaring">
      {item("bg-brand/15 border border-brand/30", "Ledig tid")}
      {item("bg-brand", "Bokat")}
      {item(
        "border border-surfaceMuted bg-[repeating-linear-gradient(135deg,transparent_0_3px,rgba(120,120,120,0.25)_3px_4px)]",
        "Ej bokningsbar tid",
      )}
      <span className="inline-flex items-center gap-xs text-xs text-textSecondary">
        <Flag aria-hidden className="h-3 w-3" /> Medicinska flaggor
      </span>
      <span className="inline-flex items-center gap-xs text-xs text-textSecondary">
        <FileX aria-hidden className="h-3 w-3" /> Underlag saknas
      </span>
    </div>
  );
}
